import {injectable, inject} from 'inversify';
import {Point} from 'openfin/_v2/api/system/point';
import {RVMInfo} from 'openfin/_v2/api/system/rvm';
import {Identity} from 'openfin/_v2/main';
import {deregister} from 'openfin-layouts';
import {withStrictTimeout} from 'openfin-service-async';
import {AppRoute} from '@openfin/desktop-ui';

import {SetLaunchBarVisibility, SetLaunchBarSize, SetLaunchBarWindowState} from '../store/partials/LaunchBar';
import {RootState} from '../store/State';
import {Action} from '../store/Store';
import {Monitor} from '../model/Monitor';
import {ServiceStore} from '../store/ServiceStore';
import {WebWindow, WebWindowFactory} from '../model/WebWindow';
import {Inject} from '../common/Injectables';
import {PostLogin, Authenticated} from '../store/partials/Authentication';
import {Dimensions} from '../types/interfaces';
import {launchBarHistory} from '../view/contexts/LaunchBarHistory';
import {ROUTES} from '../components/Router/consts';
import {LaunchBarSize, LAUNCH_BAR_DIMENSIONS, WindowState} from '../view/utils/constants';
import {LAUNCHBAR_IDENTITY, SYSTEM_PANEL_IDENTITY, SYSTEM_PANEL_MANIFEST, SYSTEM_PANEL_RVM_VERSION} from '../constants';
import {UpdateSettings, SetLaunchBarDocked} from '../store/partials/Settings';
import {renderRouterInWindow} from '../view/utils/RenderApp';
import {routes, omnibarRoutes} from '../components/Router/routes';
import {isOmnibar} from '../utils/processHelpers';

import {AsyncInit} from './AsyncInit';

export type LaunchBarDimensions = Record<keyof typeof LaunchBarSize, Dimensions>;

@injectable()
export class LaunchBar extends AsyncInit {
    public static readonly DIMENSIONS: LaunchBarDimensions = LAUNCH_BAR_DIMENSIONS;

    public static readonly IDENTITY: Identity = LAUNCHBAR_IDENTITY;

    private _webWindow!: WebWindow;

    public constructor(
        @inject(Inject.MONITOR) private readonly monitorModel: Monitor,
        @inject(Inject.STORE) private readonly store: ServiceStore,
        @inject(Inject.WEB_WINDOW_FACTORY) private readonly webWindowFactory: WebWindowFactory
    ) {
        super();

        this.store.onAction.add(this.onAction, this);
    }

    protected async init() {
        await this.store.initialized;
        await this.monitorModel.initialized;
        this._webWindow = this.webWindowFactory.wrap(LaunchBar.IDENTITY);
        await this.hideWindow();
        this.addListeners();
        await this.resizeWindow(this.store.state.launchBar.size);
        this.render();

        deregister({uuid: LAUNCHBAR_IDENTITY.uuid, name: {expression: '.*'}});
        if (this.visible) {
            await this.showWindow();
        }

        await this.startSystemPanel();

        this.registerHotkeys();
    }

    /**
     * The window visibility state
     */
    public get visible(): boolean {
        return this.store.state.launchBar.visible;
    }

    public get windowState(): WindowState {
        return this.store.state.launchBar.windowState;
    }

    public get size(): LaunchBarSize {
        return this.store.state.launchBar.size;
    }

    public get docked(): boolean {
        return this.store.state.settings.launcherDocked;
    }

    private get isLoggedIn(): boolean {
        return this.store.state.authentication.loggedIn;
    }

    private render(): void {
        const appRoutes: AppRoute[] = isOmnibar() ? omnibarRoutes : routes;
        renderRouterInWindow(appRoutes, this._webWindow, this.store, launchBarHistory, this._webWindow.document.getElementById('main')!);
        launchBarHistory.push(ROUTES.HOME);
        console.log(`Rendering ${isOmnibar() ? 'Search Only' : 'Enterprise'}`);
    }

    /**
     * Add listeners to the window.
     */
    private addListeners(): void {
        this.monitorModel.onMonitorInfoChanged.add(async () => {
            if (this.docked) {
                await this.dockWindow();
            }
        });

        this._webWindow.onRestore.add(() => {
            if (this.windowState !== WindowState.NORMAL) {
                this.store.dispatch(new SetLaunchBarWindowState(WindowState.NORMAL));
            }
        });

        this._webWindow.onMinimize.add(() => {
            if (this.windowState !== WindowState.MINIMIZED) {
                this.store.dispatch(new SetLaunchBarWindowState(WindowState.MINIMIZED));
            }
        });

        fin.Application.getCurrentSync().addListener('run-requested', (event) => {
            if (event.userAppConfigArgs && event.userAppConfigArgs.toggleWindowState === true && this.isLoggedIn) {
                if (this.windowState !== WindowState.NORMAL) {
                    this.store.dispatch(new SetLaunchBarWindowState(WindowState.NORMAL));
                } else {
                    this.store.dispatch(new SetLaunchBarWindowState(WindowState.MINIMIZED));
                }
            }
        });
    }

    private registerHotkeys(): void {
        // We need to use a document event listener instead of `fin.GlobalHotkey.register`
        // so propagation can be stopped by components who want to capture escape.
        this._webWindow.document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                launchBarHistory.push(ROUTES.HOME);
            }
        });
    }

    private async startSystemPanel(): Promise<void> {
        if (!await this.isSystemPanelPresent()) {
            // Manually start the system panel
            console.log('Starting system panel');
            await fin.Application.startFromManifest(SYSTEM_PANEL_MANIFEST).catch((error) => {
                console.warn('Error starting system panel:', error);
            });
        }
    }

    private async isSystemPanelPresent(): Promise<boolean> {
        const rvmInfo: RVMInfo | null = await withStrictTimeout(1000, fin.System.getRvmInfo(), 'RVM unavailable').catch(() => null);
        const rvmVersion = (rvmInfo ? rvmInfo.version : '0.0.0.0').split('.').map((n) => parseInt(n));
        const trayIconEnabled = rvmVersion.every((segment, index) => (segment >= SYSTEM_PANEL_RVM_VERSION[index]));

        if (trayIconEnabled) {
            // System panel may not be running, but it's tray icon should always be present
            return true;
        } else {
            // Persistant tray icon likely not present, but panel could still be running
            return fin.Application.wrapSync(SYSTEM_PANEL_IDENTITY).isRunning();
        }
    }

    private async onAction(action: Action<RootState>): Promise<void> {
        if (action instanceof SetLaunchBarVisibility) {
            await this.toggleVisibility(this.visible);
        } else if (action instanceof SetLaunchBarWindowState) {
            await this.toggleWindowState(action.windowState);
        } else if (action instanceof SetLaunchBarDocked) {
            if (action.docked) {
                await this.dockWindow();
            }
        } else if (action instanceof UpdateSettings) {
            if (action.settings.launcherDocked) {
                await this.dockWindow();
            }
        } else if (action instanceof SetLaunchBarSize) {
            await this.resizeWindow(action.size);
        } else if (action instanceof Authenticated) {
            if (!action.loggedIn) {
                await this.hideWindow();
                launchBarHistory.push(ROUTES.HOME);
            }
        } else if (action instanceof PostLogin) {
            await this.store.dispatch(new SetLaunchBarVisibility(true));
            await this.store.dispatch(new SetLaunchBarWindowState(WindowState.NORMAL));
        }
    }

    /**
     * Show the window.
     */
    public async showWindow(): Promise<void> {
        await this._webWindow.show();
        await this._webWindow.setAsForeground();
    }

    public async minimizeWindow(): Promise<void> {
        await this._webWindow.minimize();
    }

    public async restoreWindow(): Promise<void> {
        await this._webWindow.restore();
    }

    /**
     * Hide the window.
     */
    public async hideWindow(force?: boolean): Promise<void> {
        await this._webWindow.hide();
    }

    /**
     * Dock window to the top of the primary monitor.
     */
    public async dockWindow(): Promise<void> {
        const webWindow = this._webWindow;
        const {availableRect} = this.monitorModel.monitorInfo.primaryMonitor;
        const position: Point = {
            y: availableRect.top,
            x: (availableRect.right / 2) - (LAUNCH_BAR_DIMENSIONS.EXPANDED.width / 2)
        };
        await webWindow.moveTo(position);
    }

    private async toggleWindowState(windowState: WindowState): Promise<void> {
        if (windowState === WindowState.NORMAL) {
            await this._webWindow.restore();
        } else {
            await this._webWindow.minimize();
        }
    }

    /**
     * Toggle window visibility.
     * @param visible Force the window to be shown or hidden. True = show, false = hide.
     */
    private async toggleVisibility(visible: boolean): Promise<void> {
        if (visible) {
            return this.showWindow();
        } else {
            return this.hideWindow();
        }
    }

    private resizeWindow(size: LaunchBarSize): Promise<void> {
        const dimensions = LAUNCH_BAR_DIMENSIONS[LaunchBarSize[size]];
        const duration = size === LaunchBarSize.COLLAPSED ? 200 : 100;
        return this._webWindow.animate(
            {
                size: {
                    ...dimensions,
                    duration
                }
            },
            {
                interrupt: true,
                tween: 'ease-in'
            }
        );
    }
}
